<template>
  <div>
    <b-card
      v-if="customerData"
      no-body
    >
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            xl="12"
            class="mb-2"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h3 class="mb-0">
                  <b-badge
                    variant="primary"
                    pill
                    class="badge-lg"
                  >
                    #{{ customerData.id }} - {{ customerData.name }}
                  </b-badge>
                </h3>
                <b-badge
                  :variant="customerData.status === 'Y' ? 'light-success' : 'light-danger'"
                  class="mt-1"
                >
                  {{ customerData.status === 'Y' ? 'Activo' : 'Inactivo' }}
                </b-badge>
              </div>
              <div>
                <b-button
                  v-b-tooltip.hover
                  variant="flat-dark"
                  title="Editar cliente"
                  @click="showEditSidebar = true"
                >
                  <feather-icon
                    icon="Edit2Icon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Editar</span>
                </b-button>
                <b-button
                  v-b-tooltip.hover
                  variant="flat-danger"
                  title="Eliminar cliente"
                  @click="deleteCustomer"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Eliminar</span>
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <!-- Información General -->
          <b-col
            md="6"
            sm="12"
          >
            <h4 class="mb-2 mt-1">
              <feather-icon
                icon="UserIcon"
                size="20"
                class="mr-1"
              />
              Información General
            </h4>
            <b-list-group striped>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="TagIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Nombre:</strong>
                </div>
                <span>{{ customerData.name }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="CreditCardIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>NIT:</strong>
                </div>
                <span>{{ customerData.nit }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="FileTextIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Nombre del NIT:</strong>
                </div>
                <span>{{ customerData.nit_name }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Fecha de creación:</strong>
                </div>
                <span>{{ customerData.created_at }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-col>

          <!-- Información de Contacto -->
          <b-col
            md="6"
            sm="12"
          >
            <h4 class="mb-2 mt-1">
              <feather-icon
                icon="PhoneIcon"
                size="20"
                class="mr-1"
              />
              Información de Contacto
            </h4>
            <b-list-group striped>
              <b-list-group-item
                v-if="customerData.cellphone"
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <feather-icon
                    icon="SmartphoneIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Celular:</strong>
                </div>
                <span>{{ customerData.cellphone }}</span>
              </b-list-group-item>
              <b-list-group-item
                v-if="customerData.phone"
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <feather-icon
                    icon="PhoneIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Teléfono:</strong>
                </div>
                <span>{{ customerData.phone }}</span>
              </b-list-group-item>
              <b-list-group-item
                v-if="customerData.email"
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <feather-icon
                    icon="MailIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Email:</strong>
                </div>
                <span>{{ customerData.email }}</span>
              </b-list-group-item>
              <b-list-group-item
                v-if="customerData.origin"
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <feather-icon
                    icon="GlobeIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Origen:</strong>
                </div>
                <span>{{ getOriginLabel(customerData.origin) }}</span>
              </b-list-group-item>
              <b-list-group-item
                v-if="customerData.customer_type"
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <feather-icon
                    icon="TagIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Tipo de cliente:</strong>
                </div>
                <b-badge
                  :variant="getCustomerTypeVariant(customerData.customer_type)"
                  pill
                >
                  {{ getCustomerTypeLabel(customerData.customer_type) }}
                </b-badge>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>

        <!-- Ruta -->
        <b-row
          v-if="isAdmin"
          class="mt-3"
        >
          <b-col cols="12">
            <h4 class="mb-2">
              <feather-icon
                icon="MapIcon"
                size="20"
                class="mr-1"
              />
              Ruta
            </h4>
            <b-list-group striped>
              <b-list-group-item
                v-if="customerData.route"
                class="d-flex align-items-center"
              >
                <b-badge
                  variant="primary"
                  class="mr-2"
                >
                  {{ customerData.route.name }}
                </b-badge>
                <small class="text-muted">
                  <feather-icon
                    icon="UserIcon"
                    size="13"
                    class="mr-25"
                  />
                  Vendedor:
                  <strong v-if="customerData.route.user && customerData.route.user.name">
                    {{ customerData.route.user.name }}
                  </strong>
                  <span v-else>Sin vendedor</span>
                </small>
              </b-list-group-item>
              <b-list-group-item
                v-else
                class="text-muted"
              >
                <feather-icon
                  icon="AlertCircleIcon"
                  size="16"
                  class="mr-1"
                />
                Sin ruta asignada
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>

        <!-- Lista de Precios -->
        <b-row class="mt-3">
          <b-col cols="12">
            <h4 class="mb-2">
              <feather-icon
                icon="DollarSignIcon"
                size="20"
                class="mr-1"
              />
              Lista de Precios
            </h4>
            <b-list-group striped>
              <b-list-group-item
                v-if="customerData.price_list"
                class="d-flex align-items-center"
              >
                <div class="d-flex align-items-center">
                  <b-badge
                    variant="primary"
                    class="mr-2"
                  >
                    {{ customerData.price_list.name }}
                  </b-badge>
                  <small class="text-muted">
                    ({{ getPriceListTypeLabel(customerData.price_list.type_formula) }})
                  </small>
                </div>
              </b-list-group-item>
              <b-list-group-item
                v-else
                class="text-muted"
              >
                <feather-icon
                  icon="AlertCircleIcon"
                  size="16"
                  class="mr-1"
                />
                No hay lista de precios asignada
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Direcciones -->
    <b-card
      no-body
    >
      <b-card-body>
        <b-row>
          <b-col cols="12">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h4 class="mb-0">
                <feather-icon
                  icon="MapPinIcon"
                  size="20"
                  class="mr-1"
                />
                Direcciones
              </h4>
              <b-button
                v-b-tooltip.hover
                variant="flat-dark"
                title="Agregar dirección"
                @click="openAddressSidebar()"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-50"
                />
                <span>Agregar</span>
              </b-button>
            </div>

            <!-- Tabla de direcciones -->
            <b-table
              v-if="addresses.length > 0"
              :items="addresses"
              :fields="addressFields"
              hover
              responsive
              class="mb-0"
              :tbody-tr-class="row => row.is_default ? 'table-primary-light' : ''"
            >
              <!-- Columna de nombre con badge de predeterminado -->
              <template #cell(name)="data">
                <div class="d-flex align-items-center">
                  {{ data.item.name }}
                  <b-badge
                    v-if="data.item.is_default"
                    v-b-tooltip.hover
                    variant="primary"
                    pill
                    class="ml-1"
                    title="Dirección predeterminada"
                  >
                    <feather-icon
                      icon="CheckIcon"
                      size="12"
                    />
                  </b-badge>
                </div>
              </template>

              <!-- Columna de acciones -->
              <template #cell(actions)="data">
                <div class="text-nowrap">
                  <b-button
                    v-b-tooltip.hover
                    variant="flat-dark"
                    class="btn-icon mr-1"
                    title="Editar dirección"
                    @click="editAddress(data.item)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="16"
                    />
                  </b-button>
                  <b-button
                    v-b-tooltip.hover
                    variant="flat-danger"
                    class="btn-icon"
                    title="Eliminar dirección"
                    @click="deleteAddress(data.item.id)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="16"
                    />
                  </b-button>
                </div>
              </template>
            </b-table>

            <!-- Mensaje cuando no hay direcciones -->
            <div
              v-else
              class="text-center p-2"
            >
              <h5 class="mb-1 text-muted">
                <feather-icon
                  icon="MapIcon"
                  size="21"
                  class="mr-1"
                />
                No hay direcciones registradas
              </h5>
              <p class="mb-0">
                Agrega una nueva dirección usando el botón superior.
              </p>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Modal de dirección -->
    <customers-address-form
      :show.sync="showAddressModal"
      :customer-id="customerId"
      :address-data="selectedAddress"
      :customer-data="customerData"
      @refresh-data="getAddresses"
    />

    <!-- Sidebar de edición -->
    <customers-edit
      :customer-id="customerId"
      :customer-edit-sidebar-active.sync="showEditSidebar"
      @refresh-data="getCustomerData"
    />
  </div>
</template>

<script>
import axios from '@axios'
import { isAdmin } from '@/utils/auth'
import CustomersAddressForm from './CustomersAddressForm.vue'
import CustomersEdit from './CustomersEdit.vue'

export default {
  components: {
    CustomersAddressForm,
    CustomersEdit,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      customerData: null,
      isAdmin: false,
      showAddressModal: false,
      showEditSidebar: false,
      addresses: [],
      selectedAddress: null,
      addressFields: [
        { key: 'name', label: 'Dirección' },
        { key: 'receiver_name', label: 'Recibe' },
        { key: 'phone', label: 'Celular' },
        { key: 'department', label: 'Departamento' },
        { key: 'municipality', label: 'Municipio' },
        { key: 'address', label: 'Ubicación' },
        { key: 'reference', label: 'Referencia' },
        { key: 'actions', label: 'Acciones' },
      ],
    }
  },

  created() {
    this.isAdmin = isAdmin()
    this.getCustomerData()
    this.getAddresses()
  },

  methods: {
    getOriginLabel(origin) {
      const originLabels = {
        pagina_web: 'Página Web',
        redes_sociales: 'Redes Sociales',
        referido: 'Referido',
        evento: 'Evento',
        publicidad: 'Publicidad',
        otros: 'Otros',
      }
      return originLabels[origin] || origin
    },

    getPriceListTypeLabel(type) {
      const typeLabels = {
        percentage: 'Porcentaje',
        fixed: 'Monto fijo',
        none: 'Sin descuento',
      }
      return typeLabels[type] || type
    },

    getCustomerTypeLabel(type) {
      const map = {
        cliente_final: 'Cliente final',
        cliente_online: 'Cliente Online',
        excedentes: 'Excedentes',
      }
      return map[type] || type
    },

    getCustomerTypeVariant(type) {
      const variants = {
        cliente_final: 'light-success',
        cliente_online: 'light-info',
        excedentes: 'light-warning',
      }
      return variants[type] || 'light-secondary'
    },

    getCustomerData() {
      axios
        .get(`customer/${this.customerId}`)
        .then(response => {
          this.customerData = response.data.data
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    getAddresses() {
      axios
        .get(`customer/${this.customerId}/addresses`)
        .then(response => {
          this.addresses = response.data.data
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    openAddressSidebar(address = null) {
      this.selectedAddress = address
      this.showAddressModal = true
    },

    editAddress(address) {
      this.openAddressSidebar({ ...address })
    },

    deleteAddress(addressId) {
      this.$bvModal
        .msgBoxConfirm('¿Estás seguro de eliminar esta dirección?', {
          title: 'Confirmar eliminación',
          size: 'sm',
          okVariant: 'danger',
          cancelVariant: 'outline-secondary',
          cancelTitle: 'Cancelar',
          okTitle: 'Eliminar',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            axios
              .delete(`customer/${this.customerId}/addresses/${addressId}`)
              .then(() => {
                this.makeToast('success', 'Dirección eliminada', 'La dirección ha sido eliminada exitosamente.')
                this.getAddresses()
              })
              .catch(error => {
                this.showErrors(error)
              })
          }
        })
    },

    deleteCustomer() {
      this.$bvModal
        .msgBoxConfirm('¿Estás seguro de eliminar este cliente?', {
          title: 'Confirmar eliminación',
          size: 'sm',
          okVariant: 'danger',
          cancelVariant: 'outline-secondary',
          cancelTitle: 'Cancelar',
          okTitle: 'Eliminar',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            axios
              .delete(`customer/${this.customerId}`)
              .then(response => {
                this.makeToast('success', 'Cliente eliminado', response.data.msg)
                this.$emit('closeTab', this.customerId)
              })
              .catch(error => {
                this.showErrors(error)
              })
          }
        })
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },
  },
}
</script>

<style lang="scss">
.badge-secondary {
  background-color: #838383 !important;
  color: white !important;
}

.table-primary-light {
  background-color: #e8f5fe !important;
}

.table tbody tr.table-primary-light:hover {
  background-color: #d1ebfd !important;
}
</style>
